/**
 * AddressErrorHandler
 *
 * Manages error display in the UI with proper accessibility support.
 * Creates and manages an error container element with ARIA attributes.
 *
 * @example
 * const errorHandler = new AddressErrorHandler(".js-error-container");
 * errorHandler.show("Address validation failed");
 * errorHandler.clear();
 */

export default class AddressErrorHandler {
  #containerSelector;

  #errorElement = null;

  #DELAY_MS = 100;

  /**
   * Creates a new AddressErrorHandler instance
   *
   * @param {string} containerSelector - CSS selector for the container where errors will be displayed
   */
  constructor(containerSelector) {
    this.#containerSelector = containerSelector;

    if (!this.#containerSelector) {
      throw new Error("Error container selector is required");
    }

    if (!this.#containerElement) {
      throw new Error(`Error container element not found: ${containerSelector}`);
    }
  }

  /**
   * Gets the container element
   *
   * @returns {HTMLElement|null} The container element
   * @private
   */
  get #containerElement() {
    return document.querySelector(this.#containerSelector);
  }

  /**
   * Shows error message(s) to the user
   * Creates the error element if it doesn't exist
   *
   * @param {string|string[]|Object} errors - Error message(s) to display
   */
  show(errors) {
    if (!errors) {
      return;
    }

    // Ensure error element exists
    if (!this.#errorElement) {
      this.#createErrorElement();
    }

    // Normalize errors to array
    const errorArray = this.#normalizeErrors(errors);

    // Filter out empty errors
    const validErrors = errorArray.filter(
      err => err && typeof err === "string" && err.trim().length > 0
    );

    if (validErrors.length === 0) {
      this.clear();
      return;
    }

    // Build error content
    if (validErrors.length === 1) {
      // Single error - display as text
      this.#errorElement.innerHTML = this.#escapeHtml(validErrors[0]);
    } else {
      // Multiple errors - display as list
      const ul = document.createElement("ul");
      ul.className = "address-saver__error-list";

      validErrors.forEach(error => {
        const li = document.createElement("li");
        li.className = "address-saver__error-list-item";
        li.textContent = error;
        ul.appendChild(li);
      });

      this.#errorElement.innerHTML = ""; // Clear existing content
      this.#errorElement.appendChild(ul);
    }

    // Show the error element
    this.#errorElement.classList.remove("hidden");

    // Scroll to error (optional, for better UX)
    this.#scrollToError();
  }

  /**
   * Normalizes various error formats into a flat array of strings
   *
   * @param {string|string[]|Object} errors - Errors in any format
   * @returns {string[]} Flat array of error strings
   * @private
   */
  // eslint-disable-next-line class-methods-use-this
  #normalizeErrors(errors) {
    if (Array.isArray(errors)) {
      return errors;
    }

    if (typeof errors === "string") {
      return [errors];
    }

    if (typeof errors === "object" && errors !== null) {
      // Handle { base: ["error1", "error2"] } or { field: "error", field2: ["error"] }
      const errorMessages = [];

      Object.values(errors).forEach(value => {
        if (Array.isArray(value)) {
          errorMessages.push(...value);
        } else if (typeof value === "string") {
          errorMessages.push(value);
        }
      });

      return errorMessages;
    }

    return [];
  }

  /**
   * Clears all error messages and hides the error element
   */
  clear() {
    if (this.#errorElement) {
      this.#errorElement.innerHTML = "";
      this.#errorElement.classList.add("hidden");
    }
  }

  /**
   * Destroys the error handler and removes the error element from DOM
   */
  destroy() {
    if (this.#errorElement && this.#errorElement.parentNode) {
      this.#errorElement.parentNode.removeChild(this.#errorElement);
      this.#errorElement = null;
    }
  }

  /**
   * Creates the error element and appends it to the container
   *
   * @private
   */
  #createErrorElement() {
    this.#errorElement = document.createElement("div");
    this.#errorElement.className =
      "address-saver__error-container hidden mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-800";

    // Add ARIA attributes for accessibility
    this.#errorElement.setAttribute("role", "alert");
    this.#errorElement.setAttribute("aria-live", "polite");
    this.#errorElement.setAttribute("data-testid", "address-saver-error-container");

    // Insert at the beginning of the container
    if (this.#containerElement.firstChild) {
      this.#containerElement.insertBefore(this.#errorElement, this.#containerElement.firstChild);
    } else {
      this.#containerElement.appendChild(this.#errorElement);
    }
  }

  /**
   * Escapes HTML to prevent XSS attacks
   *
   * @param {string} text - Text to escape
   * @returns {string} Escaped text
   * @private
   */
  // eslint-disable-next-line class-methods-use-this
  #escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Scrolls the error element into view
   *
   * @private
   */
  #scrollToError() {
    if (!this.#errorElement) return;

    // Small delay to ensure the element is rendered
    setTimeout(() => {
      this.#errorElement.scrollIntoView({
        behavior: "smooth",
        block: "nearest"
      });
    }, this.#DELAY_MS);
  }

  /**
   * Checks if errors are currently displayed
   *
   * @returns {boolean} True if errors are displayed
   */
  hasErrors() {
    return (
      this.#errorElement &&
      !this.#errorElement.classList.contains("hidden") &&
      this.#errorElement.innerHTML.trim().length > 0
    );
  }
}
